import * as React from 'react'

import { connect } from 'react-redux'
import { RouteConfigComponentProps } from 'react-router-config'
import { Dispatch } from 'redux'

import { fetchNewestItemsByCategory } from '../../../common/actions/home/fetchNewestItemsByCategory'
import { fetchHomePageDataBySite } from '../../../common/actions/homePage'
import { setUserVideoPlayerStatus } from '../../../common/actions/user/preference'
import { reportProductClick } from '../../../common/analytics/resultAnalytics'
import { IRootState } from '../../../common/reducers'
import { ILocationParams } from '../../../common/reducers/reactRouterLocation'
import { decorateSearchResult } from '../../../common/selectors/searchResults'
import { hasFeature } from '../../../common/utils/features'
import makeSearchLink from '../../../common/utils/location/makeSearchLink'
import { makeLink } from '../../../common/utils/urlHelper'

import { Site } from '../../../common/constants/siteList'
import { IPageComponent } from '../../../typings/pageComponent'
import { IItem } from '../../../typings/searchResults'
import { ISeoMetadata } from '../../../typings/seoMetadata'

export interface INewestItemsData {
  items: IItem[]
  itemsCategory: string
  itemsPendingCategory: string | undefined
  loadingCachedCategory: boolean
}

interface IOwnProps extends RouteConfigComponentProps<any> {
  location: any // TODO: REMOVE THIS WHEN LOCATION IS NOT ADDING QUERY
}

interface IStateProps {
  isSignedIn: boolean
  currentSite: Site
  metadata: ISeoMetadata | undefined
  newestItemsData: INewestItemsData
  featuredItems: IItem[]
  freeItems: IItem[]
  preferences: any
  reportItemClick: (index: number, item: IItem, listName: string) => any
  makeSearchLink: (params: ILocationParams, currentSite?: Site) => string
}

interface IDispatchProps {
  fetchNewestItemsByCategory: (category: string) => any
  muteVideo: () => any
}

const mapStateToProps = (state: IRootState): IStateProps => {
  const {
    common: { currentSite },
    user: { isSignedIn, preferences },
    homePage: {
      newestItems: newestItemMatches,
      metadata,
      newestItemsCategory,
      newestItemsPendingCategory,
      featuredItems: featuredItemsMatches,
      freeItems: freeItemsMatches
    },
    router: { location },
    experiments
  } = state

  let newestItems: IItem[] = []
  let loadingCachedCategory = false

  if (newestItemMatches) {
    const newestItemMatchesForPendingCategory =
      newestItemsPendingCategory &&
      newestItemMatches[newestItemsPendingCategory]
    loadingCachedCategory = !!newestItemMatchesForPendingCategory
    const newestItemMatchesForCategory =
      newestItemMatchesForPendingCategory ||
      newestItemMatches[newestItemsCategory]
    newestItems =
      (newestItemMatchesForCategory &&
        newestItemMatchesForCategory.matches.map(match =>
          decorateSearchResult(state, match)
        )) ||
      []
  }

  const featuredItems: IItem[] =
    (featuredItemsMatches &&
      featuredItemsMatches.matches.map(match =>
        decorateSearchResult(state, match)
      )) ||
    []
  const freeItems: IItem[] =
    (freeItemsMatches &&
      freeItemsMatches.matches.map(match =>
        decorateSearchResult(state, match)
      )) ||
    []

  const newRoutesEnabled = hasFeature(
    'routes:new',
    currentSite,
    location,
    experiments
  )

  return {
    currentSite,
    metadata,
    isSignedIn,
    newestItemsData: {
      loadingCachedCategory,
      items: newestItems,
      itemsCategory: newestItemsCategory,
      itemsPendingCategory: newestItemsPendingCategory
    },
    featuredItems,
    freeItems,
    preferences,
    reportItemClick: (index: number, item: IItem, listName: string) => {
      return reportProductClick(index, item, currentSite, listName)
    },
    makeSearchLink: newRoutesEnabled ? makeSearchLink : makeLink
  }
}

const mapDispatchToProps = (
  dispatch: Dispatch<IRootState>,
  ownProps: IOwnProps
): IDispatchProps => {
  return {
    fetchNewestItemsByCategory: (category: string) =>
      dispatch(fetchNewestItemsByCategory(category) as any),
    muteVideo: () => dispatch(setUserVideoPlayerStatus(true))
  }
}

export type Props = IOwnProps & IDispatchProps & IStateProps

const createHomePageContainer = (component: React.ComponentType<Props>) => {
  const pageComponent: IPageComponent<Props> = component as IPageComponent<
    Props
  >
  pageComponent.loadData = (store, location, apiUrlPrefix) =>
    store.dispatch(fetchHomePageDataBySite(location, apiUrlPrefix))

  return connect<IStateProps, IDispatchProps, IOwnProps, IRootState>(
    mapStateToProps,
    mapDispatchToProps
  )(pageComponent)
}

export default createHomePageContainer
